import "i18next";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import PropTypes from "prop-types";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridSearchIcon } from "@mui/x-data-grid";
import {
  searchClassTraineeProfile,
  searchClassTraineeBySearchType,
} from "services/userServices";
import { styled } from "@mui/material/styles";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { format } from "date-fns";
import PreviewIcon from "@mui/icons-material/Preview";
import { Close, OpenInNew } from "@mui/icons-material";
import Slide from "@mui/material/Slide";
import TraineeDetails from "./TraineeDetails";
import WysiwygIcon from "@mui/icons-material/Wysiwyg";
const MainHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: 18,
  fontWeight: 800,
  letterSpacing: "0.10em",
  paddingBottom: theme.spacing(1),
}));
const SubHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Urbanist, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  fontWeight: "bold",
  paddingBottom: theme.spacing(0.75),
}));
const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
export default function SearchTrainees({ setTraineeLength }) {
  const { t } = useTranslation(["SearchTrainees"], {
    useSuspense: false,
  });
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => {
    setSnack({
      open: false,
      message: t("alert."),
      severity: "",
    });
  };
  const CustomPopper = (props) => {
    return <Popper {...props} placement="bottom-start" />;
  };
  const [traineeOracleId, setTraineeOracleId] = useState(null);
  const [traineeName, setTraineeName] = useState("");
  const [classTraineeId, setClassTraineeId] = useState(null);
  const [traineeDetails, setTraineeDetails] = useState([]);
  const [traineeRosterDetails, setTraineeRosterDetails] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchCriteria, setSearchCriteria] = useState("");
  const [gridPageSize, setGridPageSize] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false); // State to manage dialog open/close

  const [scroll, setScroll] = useState("paper");
  const handleDialogOpen = (scrollType, classTraineeId) => () => {
    setDialogOpen(true);
    setScroll(scrollType);
    setClassTraineeId(classTraineeId || null);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
  };
  const columns = [
    {
      field: "classTraineeId",
      header: t("column_header.actions"),
      align: "center",
      bodyStyle: {
        textAlign: "center",
      },
      body: (rowData) => (
        <Tooltip
          title={
            <Typography variant="body2" sx={{ fontSize: "1rem" }}>
              {t("title.view_trainee")}
            </Typography>
          }
          arrow
        >
          <IconButton
            onClick={handleDialogOpen("paper", rowData.classTraineeId)}
          >
            <WysiwygIcon
              sx={{
                color: "#3299FF",
                fontSize: "1.5rem",
              }}
            />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "classRosterId",
      header: t("column_header.roster_id"),
      sortable: true,
      align: "center",
      bodyStyle: {
        textAlign: "center",
      },
      body: (rowData) => (
        <Tooltip
          title={
            <Typography variant="body2" sx={{ fontSize: "1rem" }}>
              {t("title.view_roster")}
            </Typography>
          }
          arrow
        >
          <IconButton
            sx={{
              color: "#3299FF",
              "&:hover": {
                color: "#2878CC",
              },
            }}
            onClick={() => {
              const url = `/home/searchrosters?classRequestId=${rowData.classRequestId}&classRosterId=${rowData.classRosterId}`;
              window.open(url, "_blank"); // Open in a new tab with query parameters
            }}
          >
            {rowData.classRosterId}
            <OpenInNew
              sx={{
                ml: 0.5,
              }}
              fontSize="small"
            />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "rosterStatus",
      header: t("column_header.roster_status"),
      sortable: true,
      filter: true,
      filterPlaceholder: t("filter_placeholder.search_by_roster_status"),
      align: "left",
      bodyStyle: {
        textAlign: "left",
      },
    },
    {
      field: "classRequestId",
      header: t("column_header.request_id"),
      sortable: true,
      align: "center",
      bodyStyle: {
        textAlign: "right",
      },
    },
    {
      field: "requestStatus",
      header: t("column_header.request_status"),
      sortable: true,
      filter: true,
      filterPlaceholder: t("filter_placeholder.search_by_status"),
      align: "left",
      bodyStyle: {
        textAlign: "left",
      },
    },
    {
      field: "wave",
      header: t("column_header.wave"),
      sortable: true,
      align: "left",
      bodyStyle: {
        textAlign: "right",
      },
    },
    {
      field: "techEquipmentSolution",
      header: t("column_header.tech_equipment_solution"),
      sortable: true,
      filter: true,
      filterPlaceholder: t(
        "filter_placeholder.search_by_tech_equipment_solution"
      ),
      align: "left",
      bodyStyle: {
        textAlign: "left",
      },
    },
    {
      field: "parentClientName",
      header: t("column_header.parent_client"),
      sortable: true,
      filter: true,
      filterPlaceholder: t("filter_placeholder.search_by_parent_client"),
      align: "left",
      bodyStyle: {
        textAlign: "left",
      },
    },
    {
      field: "traineeStatus",
      header: t("column_header.trainee_status"),
      sortable: true,
      filter: true,
      filterPlaceholder: t("filter_placeholder.search_by_trainee_status"),
      align: "left",
      bodyStyle: {
        textAlign: "left",
      },
    },
    {
      field: "trainerName",
      header: t("column_header.trainer_name"),
      sortable: true,
      filter: true,
      filterPlaceholder: t("filter_placeholder.search_by_trainer"),
      align: "left",
      bodyStyle: {
        textAlign: "left",
      },
      body: (rowData) =>
        rowData.trainerName
          ? `${rowData.trainerName} (${rowData.trainerId || "NA"})`
          : "NA",
    },
    {
      field: "ogName",
      header: t("column_header.og_name"),
      sortable: true,
      filter: true,
      filterPlaceholder: t("filter_placeholder.search_by_og"),
      align: "left",
      bodyStyle: {
        textAlign: "left",
      },
      body: (rowData) =>
        rowData.ogName ? `${rowData.ogName} (${rowData.ogId || "NA"})` : "NA",
    },
  ];
  const filterProps = {
    global: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    // Text Filters
    requestStatus: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_status"),
    },
    rosterStatus: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_roster_status"),
    },
    techEquipmentSolution: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t(
        "filter_placeholder.search_by_tech_equipment_solution"
      ),
    },
    traineeStatus: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_trainee_status"),
    },
    trainerName: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_trainer"),
    },
    ogName: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_og_name"),
    },
    parentClientName: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_parent_client"),
    },
    // Numeric Filters
    classRequestId: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.EQUALS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_request_id"),
    },
    classRosterId: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.EQUALS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_roster_id"),
    },
    wave: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.EQUALS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_wave"),
    },
  };
  const handleValueChange = (value) => {
    setSearchCriteria(value);
  };
  const handleSearch = async (event, searchQuery) => {
    try {
      setLoading(true);
      if (searchQuery.length < 3) {
        setSnack({
          open: true,
          message: t(
            "alert.search_criteria_must_be_at_least_3_characters_in_length_"
          ),
          severity: "error",
        });
        setLoading(false);
        return;
      }
      const searchResult = await searchClassTraineeProfile(searchQuery, 0);
      if (searchResult.status === true) {
        let searchResultDetails;
        if (/^\d+$/.test(searchQuery) && searchQuery.length >= 4) {
          searchResultDetails = searchResult.data.map((emp) => ({
            OracleId: emp.OracleId,
            Display:
              (emp.Name ?? "") + (emp.OracleId ? ` (${emp.OracleId})` : ""),
            Name: emp.Name,
            Email: emp.Email,
          }));
        } else {
          searchResultDetails = searchResult.data.map((emp) => ({
            OracleId: emp.OracleId,
            Display: emp.Display,
            Name: emp.Name,
            Email: emp.Email,
          }));
        }
        setSearchResults(searchResultDetails);
        setSnack({
          open: true,
          message: t("alert.we_found_results_matching_the_search_criteria_"),
          severity: "success",
        });
      } else {
        setSnack({
          open: true,
          message: t("alert.no_result_found_matching_with_search_criteria_"),
          severity: "error",
        });
      }
    } catch {
      setSnack({
        open: true,
        message: t("alert.an_error_occurred_while_searching_please_try_again_"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const calculatePageSize = () => {
    const availableHeight = window.innerHeight - 420; // Subtract the fixed height offset
    if (availableHeight > 1200) return 100;
    if (availableHeight > 900) return 50;
    if (availableHeight > 600) return 25;
    return 10;
  };
  const handleInputChange = (event, newInputValue) => {
    try {
      setLoading(true);
      if (newInputValue === "") {
        setTraineeOracleId(null);
        setTraineeName("");
        setTraineeDetails([]);
        setTraineeRosterDetails([]);
        setTraineeLength(0);
      }
    } catch {
      setSnack({
        open: true,
        message: t(
          "alert.an_error_occurred_while_clearing_selected_result_please_try_again_"
        ),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const handleSelectionChange = async (event, value) => {
    try {
      setLoading(true);
      if (value && value.OracleId !== undefined) {
        setTraineeOracleId(Number(value.OracleId));
        setTraineeName(value.Name);
        if (value.OracleId.toString().length === 7) {
          const traineeResponse = await searchClassTraineeBySearchType(
            "OracleId",
            value.OracleId
          );
          if (traineeResponse?.message === "Success") {
            setTraineeDetails(traineeResponse?.data?.trainees);
            setTraineeRosterDetails(traineeResponse?.data?.traineeDetails);
            setTraineeLength(
              traineeResponse?.data?.trainees[0]?.numberOfClassRosters
            );
            const updatePageSize = () => {
              setGridPageSize(calculatePageSize());
            };
            updatePageSize();
            window.addEventListener("resize", updatePageSize);
          }
        }
      }
      setSearchResults([]);
    } catch {
      setSnack({
        open: true,
        message: t(
          "alert.an_error_occurred_while_selecting_result_please_try_again_"
        ),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <SubHeading>{t("subheading.search_trainee")}</SubHeading>
          <Autocomplete
            freeSolo
            openOnFocus
            disablePortal
            options={searchResults}
            onInputChange={handleInputChange}
            onChange={handleSelectionChange}
            PopperComponent={CustomPopper}
            getOptionLabel={(option) => option.Display || ""}
            renderInput={(params) => (
              <TextField
                name="searchTrainee"
                {...params}
                size="small"
                placeholder={t(
                  "placeholder.search_by_oracle_id_or_trainee_name_or_email_address"
                )}
                value={traineeName || ""}
                onChange={(event) => handleValueChange(event.target.value)}
                onKeyPress={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleSearch(event, searchCriteria);
                  }
                }}
                sx={{
                  minWidth: 120,
                }}
                InputProps={{
                  maxLength: 50,
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                            {t("title.search")}
                          </Typography>
                        }
                      >
                        <IconButton
                          id="icon-button-search"
                          type="button"
                          onClick={(event) =>
                            handleSearch(event, searchCriteria)
                          }
                        >
                          <GridSearchIcon />
                        </IconButton>
                      </Tooltip>
                      {params.InputProps.endAdornment}
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={8}>
          &nbsp;
        </Grid>
        {traineeDetails.length > 0 && (
          <>
            <Grid item xs={12}>
              <Divider/>
            </Grid>
            <Grid item xs={1.5}>
              <SubHeading>{t("subheading.oracle_id")}</SubHeading>
              <Typography variant="subtitle1" gutterBottom>
                {Number(traineeDetails[0]?.oracleId)}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <SubHeading>
                {traineeDetails[0]?.candidateId?.includes(",")
                  ? t("subheading.candidate_ids")
                  : t("subheading.candidate_id")}
              </SubHeading>
              <Typography variant="subtitle1" gutterBottom>
                {traineeDetails[0]?.candidateId || "NA"}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <SubHeading>{t("subheading.trainee_name")}</SubHeading>
              <Typography variant="subtitle1" gutterBottom>
                {traineeDetails[0]?.traineeName}
              </Typography>
            </Grid>
            <Grid item xs={1.5}>
              <SubHeading>{t("subheading.no_of_roster_s_")}</SubHeading>
              <Typography variant="subtitle1" gutterBottom>
                {Number(traineeDetails[0]?.numberOfClassRosters) || "0"}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <SubHeading>{t("subheading.last_updated_date_time")}</SubHeading>
              <Typography variant="subtitle1" gutterBottom>
                {traineeDetails[0]?.lastUpdate
                  ? format(
                      new Date(traineeDetails[0]?.lastUpdate),
                      "dd-MMM-yyyy hh:mm:ss a"
                    )
                  : t("typography.na")}
              </Typography>
            </Grid>
            <Grid
              item
              xs={3}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
              }}
            >
              <CustomButton
                variant="contained"
                startIcon={<PreviewIcon />}
                onClick={handleDialogOpen("paper", null)}
              >
                {t("custombutton.view_details")}
              </CustomButton>
              <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                scroll={scroll}
                TransitionComponent={Transition}
                maxWidth="xl"
                fullWidth
              >
                <DialogTitle>
                  <MainHeading>
                    {t("mainheading.class_trainee_details")}
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "14px",
                        fontWeight: "normal",
                      }}
                    >
                      {" - "}
                      {traineeDetails[0]?.traineeName}{" "}
                      {traineeDetails[0]?.oracleId
                        ? `(${traineeDetails[0]?.oracleId})`
                        : t("typography.")}
                    </Typography>
                  </MainHeading>
                  <IconButton
                    aria-label={t("aria-label.close")}
                    onClick={handleDialogClose}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <Close />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers={scroll === "paper"}>
                  <TraineeDetails
                    traineeOracleId={traineeOracleId}
                    classTraineeId={classTraineeId}
                  />
                </DialogContent>
              </Dialog>
            </Grid>
          </>
        )}
        {traineeRosterDetails.length > 0 ? (
          <Grid item xs={12}>
            <PrimeReactDataGrid
              dataKey="classTraineeId"
              size="normal"
              removableSort
              resizableColumns
              showGridlines
              paginator
              rows={gridPageSize}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate={t(
                "pagination_template.showing_first_to_last_of_totalrecords_record_s_"
              )}
              columnResizeMode="expand"
              value={traineeRosterDetails}
              columns={columns}
              filterprops={filterProps}
              selectionMode="multiple"
              dragSelection
              metaKeySelection={false}
              enableGlobalFilter={false}
              enableStickyHeader={true}
              enableScrollable={true}
              scrollHeight="calc(100vh - 440px)"
              sortMode="multiple"
              emptyMessage={t("empty_message.no_records_found_")}
            ></PrimeReactDataGrid>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "calc(100vh - 440px)",
                width: "100%",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#3F5765",
                  fontWeight: "bold",
                  letterSpacing: "0.10em",
                  padding: 2,
                }}
              >
                {t("typography.no_class_trainee_details_found_")}
              </Typography>
              {traineeDetails.length === 0 && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#3F5765",
                    letterSpacing: "0.05em",
                    padding: 1,
                  }}
                >
                  {t(
                    "typography.please_search_for_class_trainee_using_oracle_id_trainee_name_and_email_address_"
                  )}
                </Typography>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
      <SnackBar snack={snack} snackReset={snackReset} />
    </>
  );
}
SearchTrainees.propTypes = {
  setTraineeLength: PropTypes.func,
};
